import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnDestroy, ViewEncapsulation, ElementRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-ngb-datepicker',
  templateUrl: './ngb-datepicker.component.html',
  styleUrls: ['./ngb-datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NgbDatepickerComponent),
      multi: true
    }
  ]
})
export class NgbDatepickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label: string = '';
  @Input() placeholder: string = this.label;
  @Input() isDisabled: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() formGroup?: FormGroup;
  @Input() controlName: string = '';
  @Input()
  set isSubmitted(value: boolean) {
    this._isSubmitted = value;
    if (value && this.control) {
      this.control.markAsTouched();
      this.control.updateValueAndValidity({ emitEvent: false });
    }
  }
  get isSubmitted(): boolean {
    return this._isSubmitted;
  }
  private _isSubmitted: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() addOptionalHint: boolean = false;
  @Input() includeTime: boolean = false;
  @Output() dateChange = new EventEmitter<Date | null>();

  // Internal state - using Date instead of NgbDate
  selectedDate: Date | null = null;
  selectedTime: string = '';

  // Track raw input during typing to prevent field reset
  private rawInputValue: string = '';

  // Form control
  control?: FormControl;
  private valueChangesSubscription?: any;
  @ViewChild('dateInput', { static: true }) dateInputElement!: ElementRef<HTMLInputElement>;

  // ControlValueAccessor callbacks
  private onChange: any = () => {};
  private onTouched: any = () => {};

  constructor(private translateService: TranslateService) {}

  // Check if current language is RTL (Arabic)
  get isRTL(): boolean {
    return this.translateService.currentLang === 'ar';
  }

  // Format Date for HTML5 date input (YYYY-MM-DD)
  get dateInputValue(): string {
    // Show raw input while typing to prevent reset
    if (this.rawInputValue) {
      return this.rawInputValue;
    }

    if (!this.selectedDate) return '';
    // Ensure selectedDate is a valid Date object
    if (!(this.selectedDate instanceof Date) || isNaN(this.selectedDate.getTime())) {
      return '';
    }
    return this.formatDateForInput(this.selectedDate);
  }

  // Format Time for HTML5 time input (HH:mm)
  get timeInputValue(): string {
    if (!this.selectedDate || !(this.selectedDate instanceof Date) || isNaN(this.selectedDate.getTime())) {
      return '';
    }
    const hours = String(this.selectedDate.getHours()).padStart(2, '0');
    const minutes = String(this.selectedDate.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  ngOnInit(): void {
    if (this.formGroup && this.controlName) {
      this.control = this.formGroup.get(this.controlName) as FormControl;
      if (!this.control) {
        console.error(`Form control with name "${this.controlName}" not found in form group`);
      } else {
        this.valueChangesSubscription = this.control.valueChanges.subscribe(value => {
          this.writeValue(value);
        });
        this.writeValue(this.control.value);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    // Clear raw input when programmatic value changes
    this.rawInputValue = '';
    if (value) {
      if (value instanceof Date) {
        this.selectedDate = value;
      } else if (typeof value === 'string') {
        // Parse timezone-neutrally if it starts with YYYY-MM-DD
        const dateTimeParts = value.split('T');
        const datePart = dateTimeParts[0];
        const parts = datePart.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const day = parseInt(parts[2], 10);
          let hours = 0;
          let minutes = 0;
          // Parse time portion if present (e.g., "20:00" or "20:00:00")
          if (dateTimeParts.length > 1) {
            const timeParts = dateTimeParts[1].split(':');
            if (timeParts.length >= 2) {
              hours = parseInt(timeParts[0], 10) || 0;
              minutes = parseInt(timeParts[1], 10) || 0;
            }
          }
          const newDate = new Date(year, month - 1, day, hours, minutes);
          // Only set if it's a valid date
          this.selectedDate = isNaN(newDate.getTime()) ? null : newDate;
        } else {
          const parsed = new Date(value);
          this.selectedDate = isNaN(parsed.getTime()) ? null : parsed;
        }
      } else if (value && typeof value === 'object' && 'year' in value && 'month' in value && 'day' in value) {
        // Handle NgbDate-like objects for backward compatibility
        const newDate = new Date(value.year, value.month - 1, value.day);
        this.selectedDate = isNaN(newDate.getTime()) ? null : newDate;
      } else {
        // Unknown format - reset to null
        this.selectedDate = null;
      }
    } else {
      this.selectedDate = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  // Handle date input change from native date picker
  onDateInputChange(value: string): void {
    this.rawInputValue = value;

    if (!value) {
      this.selectedDate = null;
      this.onChange(null);
      this.dateChange.emit(null);
      if (this.control) {
        this.control.setValue(null, { emitEvent: true });
        this.validateCurrentInput();
      }
      return;
    }

    // Only process complete dates - allow incomplete input during typing
    if (this.isPotentiallyCompleteDate(value)) {
      const parsedDate = this.parsePartialInputDate(value);

      if (parsedDate && this.isValidDate(parsedDate)) {
        let finalDate = parsedDate;
        // Preserve existing time if includeTime is true and we have a selectedDate
        if (this.includeTime && this.selectedDate && !isNaN(this.selectedDate.getTime())) {
          finalDate = new Date(
            parsedDate.getFullYear(),
            parsedDate.getMonth(),
            parsedDate.getDate(),
            this.selectedDate.getHours(),
            this.selectedDate.getMinutes()
          );
        }

        this.selectedDate = finalDate;
        this.onChange(finalDate);
        this.dateChange.emit(finalDate);
        if (this.control) {
          this.control.setValue(finalDate, { emitEvent: true });
          this.clearInvalidDateError();
        }
      } else {
        // Set invalidDate error for invalid complete dates
        if (this.control) {
          this.setInvalidDateError();
        }
      }
    }
    // Incomplete dates: do nothing, allow typing to continue
  }

  // Handle time input change from native time picker
  onTimeInputChange(value: string): void {
    if (!this.selectedDate) {
      // If no date is set yet, create a new date with today's date and the selected time
      const [hours, minutes] = value.split(':').map(Number);
      const today = new Date();
      this.selectedDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
    } else {
      // Update existing date with new time
      const [hours, minutes] = value.split(':').map(Number);
      this.selectedDate.setHours(hours, minutes);
    }

    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
    if (this.control) {
      this.control.setValue(this.selectedDate, { emitEvent: true });
    }
  }

  // Blur handler
  onBlur(): void {
    this.onTouched();

    // Clear raw input on blur to show formatted date
    this.rawInputValue = '';

    if (this.control) {
      this.control.markAsTouched();
      this.validateCurrentInput();
      this.control.updateValueAndValidity({ emitEvent: true });
    }
  }

  // Validate current input
  private validateCurrentInput(): void {
    if (!this.control) return;

    const inputValue = this.dateInputElement?.nativeElement?.value?.trim();
    let errors = this.control.errors ? { ...this.control.errors } : null;

    const setInvalidDate = (): void => {
      if (!errors) errors = {};
      errors.invalidDate = true;
      this.control!.setErrors(errors);
    };

    const clearInvalidDate = (): void => {
      if (!errors || !errors.invalidDate) return;
      delete errors.invalidDate;
      if (Object.keys(errors).length === 0) {
        errors = null;
      }
      this.control!.setErrors(errors);
    };

    if (!inputValue) {
      clearInvalidDate();
      return;
    }

    // Parse the date string
    const parsedDate = this.parseInputDate(inputValue);
    if (!parsedDate || !this.isValidDate(parsedDate)) {
      setInvalidDate();
      return;
    }

    // Check min/max constraints
    if (this.minDate && parsedDate < this.minDate) {
      setInvalidDate();
      return;
    }

    if (this.maxDate && parsedDate > this.maxDate) {
      setInvalidDate();
      return;
    }

    clearInvalidDate();
  }

  // Clear invalidDate error
  private clearInvalidDateError(): void {
    if (!this.control) return;
    let errors = this.control.errors;
    if (!errors || !errors.invalidDate) return;

    delete errors.invalidDate;
    if (Object.keys(errors).length === 0) {
      errors = null;
    }
    this.control.setErrors(errors);
  }

  // Set invalidDate error
  private setInvalidDateError(): void {
    if (!this.control) return;
    let errors = this.control.errors ? { ...this.control.errors } : null;
    if (!errors) errors = {};
    errors.invalidDate = true;
    this.control.setErrors(errors);
  }

  // Parse date string from input (YYYY-MM-DD format)
  private parseInputDate(value: string): Date | null {
    if (!value) return null;

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const day = parseInt(match[3], 10);
      return new Date(year, month - 1, day);
    }

    return null;
  }

  // Check if input is potentially complete (YYYY-MM-DD format = 10 chars)
  private isPotentiallyCompleteDate(value: string): boolean {
    return value.length === 10;
  }

  // Parse date with flexible validation for partial input handling
  private parsePartialInputDate(value: string): Date | null {
    if (!value) return null;
    const parts = value.split('-');
    if (parts.length !== 3) return null;

    const [yearStr, monthStr, dayStr] = parts;
    if (yearStr.length !== 4 || monthStr.length !== 2 || dayStr.length !== 2) return null;

    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);

    if (year < 1900 || year > 2099 || month < 1 || month > 12 || day < 1 || day > 31) return null;

    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }

  // Check if a date is valid
  private isValidDate(date: Date): boolean {
    if (!date || isNaN(date.getTime())) return false;

    const year = date.getFullYear();
    // Validate year range
    if (year < 1900 || year > 2099) return false;

    return true;
  }

  // Clear date handler
  clearDate(): void {
    this.selectedDate = null;
    this.onChange(null);
    this.dateChange.emit(null);

    if (this.control) {
      this.control.setValue(null, { emitEvent: true });
      this.control.markAsTouched();
      this.control.updateValueAndValidity({ emitEvent: true });
    }
  }

  // Format Date for HTML5 date input (YYYY-MM-DD)
  formatDateForInput(date: Date): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get hasError(): boolean {
    if (!this.control) return false;
    const errors = this.control.errors;
    if (!errors) return false;

    // Show invalidDate errors immediately
    if (errors['invalidDate']) {
      return true;
    }

    // For other errors (required, min, max), show only after touched or submitted
    return (this.isSubmitted || this.control.touched) && this.control.invalid;
  }

  get validationMessage(): string {
    if (!this.control || !this.control.errors) {
      return '';
    }

    const errors = this.control.errors;

    if (errors['required']) {
      return this.translateService.instant('validation.required');
    }

    if (errors['min']) {
      const minDateStr = this.minDate ? this.formatSelectedDateFromDate(this.minDate) : '';
      if (this.isRTL) {
        return `يجب أن يكون التاريخ بعد ${minDateStr}`;
      }
      return `Date must be after ${minDateStr}`;
    }

    if (errors['max']) {
      const maxDateStr = this.maxDate ? this.formatSelectedDateFromDate(this.maxDate) : '';
      if (this.isRTL) {
        return `يجب أن يكون التاريخ قبل ${maxDateStr}`;
      }
      return `Date must be before ${maxDateStr}`;
    }
    return '';

    //if (errors['invalidDate']) {
    //  return this.translateService.instant('validation.invalidDate');
    //}

    //if (this.isRTL) {
    //  return 'تاريخ غير صالح';
    //}
    //return 'Invalid date';
  }

  // Helper to format a Date object for display
  private formatSelectedDateFromDate(date: Date): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
}
